import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Search, Plus, Menu, X, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CITIES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/i18n";
import AuthModal from "@/components/AuthModal";
import CreateInitiativeModal from "@/components/CreateInitiativeModal";

const navItems = [
  { to: "/jelajahi", key: "nav_explore" },
  { to: "/matchmaker", key: "nav_match" },
  { to: "/solusi", key: "nav_planner" },
  { to: "/organizer", key: "nav_organizer" },
  { to: "/profil", key: "nav_profile" },
];

export default function Header() {
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { lang, setLang, t } = useLang();
  const onExplore = location.pathname === "/jelajahi";
  const activeCity = onExplore ? new URLSearchParams(location.search).get("city") : null;

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/jelajahi?search=${encodeURIComponent(query)}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
            <span className="bg-emerald-600 text-white rounded-xl p-1.5">
              <ShieldCheck className="w-5 h-5" />
            </span>
            <span className="font-heading font-black text-lg tracking-tight text-slate-900">
              Bergerak<span className="text-emerald-600">Nyata</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:text-emerald-700 hover:bg-slate-100"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div data-testid="lang-toggle" role="group" aria-label="Pilih bahasa" className="flex items-center bg-slate-100 rounded-full p-0.5">
              {["id", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  aria-pressed={lang === l}
                  className={`text-xs font-black px-2.5 py-1 rounded-full uppercase transition-colors ${
                    lang === l ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-emerald-700"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <form onSubmit={doSearch} className="hidden md:flex items-center bg-slate-100 rounded-full px-3 py-1.5">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                data-testid="header-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder")}
                className="bg-transparent outline-none text-sm px-2 w-32 lg:w-40"
              />
            </form>
            <Button
              data-testid="buat-inisiatif-btn"
              onClick={() => setCreateOpen(true)}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold hidden sm:flex"
            >
              <Plus className="w-4 h-4 mr-1" /> {t("create_initiative")}
            </Button>
            <button
              data-testid="auth-btn"
              onClick={() => setAuthOpen(true)}
              aria-label="Masuk atau kelola profil"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-bold text-sm hover:bg-emerald-100 transition-colors"
            >
              <UserCircle2 className="w-5 h-5" />
              <span className="hidden md:inline max-w-[110px] truncate">{user ? user.name.split(" ")[0] : "Masuk / Profil"}</span>
            </button>
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 pb-2">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t("city_label")}</span>
          <button
            data-testid="quick-filter-semua"
            onClick={() => navigate("/jelajahi")}
            aria-pressed={onExplore && !activeCity}
            className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
              onExplore && !activeCity
                ? "bg-emerald-600 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"
            }`}
          >
            {t("all_cities")}
          </button>
          {CITIES.map((city) => (
            <button
              key={city}
              data-testid={`quick-filter-${city.toLowerCase()}`}
              onClick={() => navigate(`/jelajahi?city=${city}`)}
              aria-pressed={activeCity === city}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
                activeCity === city
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"
              }`}
            >
              {city}
            </button>
          ))}
        </div>
      </div>

      {mobileOpen && (
        <nav data-testid="mobile-nav" className="lg:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-emerald-50"
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>
      )}

      <CreateInitiativeModal open={createOpen} onOpenChange={setCreateOpen} />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
